import PropTypes from 'prop-types'
import React from 'react'
import styled from 'styled-components'
import * as dates from 'react-big-calendar/lib/utils/dates'
import { DnDContext } from '../DnDContext'
import Droppable from './Droppable'
import { connect } from 'react-redux';
import { isEqual, closestTo, differenceInMinutes } from 'date-fns'

import Selection, {
	getBoundsForNode,
	getEventNodeFromPoint,
} from 'react-big-calendar/lib/Selection'
import TimeGridEvent from 'react-big-calendar/lib/TimeGridEvent'
import { dragAccessors, eventTimes, pointInColumn } from '../common'
import NoopWrapper from 'react-big-calendar/lib/NoopWrapper'
import { timezoneDate } from 'utils'

const Preview = styled.div`
	background-color: var(--focus-color);
	border-radius: 5px;
	padding: 0.2rem;
	width: 100%;
	height: ${props => props.height};
	position: absolute;
	top: ${props => props.y};
`

class EventContainerWrapper extends React.Component {
	static propTypes = {
		accessors: PropTypes.object.isRequired,
		components: PropTypes.object.isRequired,
		getters: PropTypes.object.isRequired,
		localizer: PropTypes.object.isRequired,
		slotMetrics: PropTypes.object.isRequired,
		resource: PropTypes.any,
	}

	static contextType = DnDContext
	
	constructor(...args) {
		super(...args)
		this.state = {}
		this.ref = React.createRef()
	}

	componentDidMount() {
		this._selectable()
	}

	componentWillUnmount() {
		this._teardownSelectable()
	}

	reset() {
		if (this.state.event)
			this.setState({ event: null, top: null, height: null })
	}

	update(event, { startDate, endDate, top, height }) {
		const { event: lastEvent } = this.state
		if (
			lastEvent &&
			startDate === lastEvent.start &&
			endDate === lastEvent.end
		) {
			return
		}

		this.setState({
			top,
			height,
			event: { ...event, start: startDate, end: endDate },
		})
	}

	handleMove = (point, bounds) => {
		if (!pointInColumn(bounds, point)) return this.reset()
		const { event } = this.context.draggable.dragAndDropAction
		const { accessors, slotMetrics } = this.props
		// console.log(bounds)
		const newSlot = slotMetrics.closestSlotFromPoint(
			{ y: point.y - this.eventOffsetTop, x: point.x },
			bounds
		)

		const { duration } = eventTimes(event, accessors)
		let newEnd = dates.add(newSlot, duration, 'milliseconds')
		this.update(event, slotMetrics.getRange(newSlot, newEnd, false, true))
	}

	handleResize(point, bounds) {
		const { accessors, slotMetrics } = this.props
		const { event, direction } = this.context.draggable.dragAndDropAction
		const newTime = slotMetrics.closestSlotFromPoint(point, bounds)

		let { start, end } = eventTimes(event, accessors)
		if (direction === 'UP') {
			start = dates.min(newTime, slotMetrics.closestSlotFromDate(end, -1))
		} else if (direction === 'DOWN') {
			end = dates.max(newTime, slotMetrics.closestSlotFromDate(start))
		}
		this.update(event, slotMetrics.getRange(start, end))
	}

	handleDropFromOutside = (point, boundaryBox) => {
		const { slotMetrics, resource } = this.props

		let start = slotMetrics.closestSlotFromPoint(
			{ y: point.y, x: point.x },
			boundaryBox
		)

		this.context.draggable.onDropFromOutside({
			start,
			end: slotMetrics.nextSlot(start),
			allDay: false,
			resource,
		})
	}

	_selectable = () => {
		let wrapper = this.ref.current
		let node = wrapper.children[0]
		let isBeingDragged = false
		let selector = (this._selector = new Selection(() =>
			wrapper.closest('.rbc-time-view')
		))

		selector.on('beforeSelect', point => {
			const { dragAndDropAction } = this.context.draggable

			if (!dragAndDropAction.action) return false
			if (dragAndDropAction.action === 'resize') {
				return pointInColumn(getBoundsForNode(node), point)
			}

			const eventNode = getEventNodeFromPoint(node, point)
			if (!eventNode) return false

			// eventOffsetTop is distance from the top of the event to the initial
			// mouseDown position. We need this later to compute the new top of the
			// event during move operations, since the final location is really a
			// delta from this point. note: if we want to DRY this with WeekWrapper,
			// probably better just to capture the mouseDown point here and do the
			// placement computation in handleMove()...
			this.eventOffsetTop = point.y - getBoundsForNode(eventNode).top
		})

		selector.on('selecting', box => {
			const bounds = getBoundsForNode(node)
			const { dragAndDropAction } = this.context.draggable

			if (dragAndDropAction.action === 'move') this.handleMove(box, bounds)
			if (dragAndDropAction.action === 'resize') this.handleResize(box, bounds)
		})

		selector.on('dropFromOutside', point => {
			if (!this.context.draggable.onDropFromOutside) return
			const bounds = getBoundsForNode(node)
			if (!pointInColumn(bounds, point)) return
			this.handleDropFromOutside(point, bounds)
		})

		selector.on('dragOver', point => {
			if (!this.context.draggable.dragFromOutsideItem) return
			const bounds = getBoundsForNode(node)
			this.handleDropFromOutside(point, bounds)
		})

		selector.on('selectStart', () => {
			isBeingDragged = true
			this.context.draggable.onStart()
		})

		selector.on('select', point => {
			const bounds = getBoundsForNode(node)
			isBeingDragged = false
			if (!this.state.event || !pointInColumn(bounds, point)) return
			this.handleInteractionEnd()
		})

		selector.on('click', () => {
			if (isBeingDragged) this.reset()
			this.context.draggable.onEnd(null)
		})

		selector.on('reset', () => {
			this.reset()
			this.context.draggable.onEnd(null)
		})
	}

	handleInteractionEnd = () => {
		const { resource } = this.props
		const { event } = this.state
		this.reset()

		this.context.draggable.onEnd({
			start: event.start,
			end: event.end,
			resourceId: resource,
		})
	}

	_teardownSelectable = () => {
		if (!this._selector) return
		this._selector.teardown()
		this._selector = null
	}

	getSlot = (clientOffset, duration) => {
		const boundingRect = this.ref.current.children[0].getBoundingClientRect()
		const { x, y } = clientOffset
		const { accessors, slotMetrics } = this.props

		// Get position
		let newSlot = slotMetrics.closestSlotFromPoint(
			{ y: y , x: x },
			boundingRect
		)
		// newSlot = moment(format(newSlot, 'Y-MM-dd HH:mm:ss')).tz(this.props.user['timezone'])
		newSlot = timezoneDate(newSlot, this.props.user['timezone'])

		// Return schedule
		const date = newSlot.clone().set({hour: 0, minute: 0, second: 0})
		const end_time = newSlot.clone().add(duration)
		const schedule = {
			start_date: date,
			end_date: date,
			start_time: newSlot,
			end_time: duration ? end_time : newSlot
		}

		// Debug
		const range = boundingRect.top - boundingRect.bottom
		const position = Math.floor(Math.abs((y  - boundingRect.top) / range) * 48)
		const offsetY = Math.abs((range/48)*position)
		// console.log('[EventContainerWrapper onDrop]', {position: position, offsetY: offsetY, top: boundingRect.top, y: y})

		return {schedule: schedule, offsetY: offsetY, size: Math.abs(range/48)}
	}

	onHoverHandler = (id, item, clientOffset) => {

		const slot = this.getSlot(clientOffset, this.context.extraProps['scheduleDuration'])
		if(!this.state.preview){
			this.setState({preview: { schedule: slot['schedule'], offsetY: slot['offsetY'], size: slot['size'] }})
		} else {
			const oldStart = this.state.preview['schedule']['start_time'] && this.state.preview['schedule']['start_time'].format('D-M-Y h:mma')
			const updatedStart = slot['schedule']['start_time'] && slot['schedule']['start_time'].format('D-M-Y h:mma')
			oldStart !== updatedStart && this.setState({preview: { schedule: slot['schedule'], offsetY: slot['offsetY'], size: slot['size'] }})
		}
	}

	onDropHandler = (id, item, clientOffset) => {
		const duration = this.context.extraProps['scheduleDuration']
		let schedule = this.getSlot(clientOffset, this.context.extraProps['scheduleDuration'])['schedule']		

		// Return schedule
		const timezone = this.props.user['timezone']
		schedule = {
            start_date: timezoneDate(schedule['start_date'], timezone),
            start_time: timezoneDate(schedule['start_time'], timezone),
            end_date: timezoneDate(schedule['end_date'], timezone),
            end_time: timezoneDate(schedule['end_time'], timezone),
        }
		this.context.extraProps.onExtEventDrop(id, item.props.item, schedule)
		// this.setState({preview: null})
	}

	getPreview = () => {
		let values = {
			size: null,
			style: null,
			value: null
		}

		if(this.state.preview){
			const start = this.state.preview['schedule']['start_time'].format('h:mm')
			// const end = format(this.state.preview['schedule']['end_time'], 'h:mmaaa').toLowerCase()
			const end = this.state.preview['schedule']['end_time'].format('h:mma')
			values['size'] = this.state.preview['size']
			values['style'] = {height: `calc(${this.state.preview['size']*2}px)`}
			values['value'] = `${start} - ${end}`
		} else if (this.state.top || this.state.top === 0) {
			const { draggable, slotHandlers, extraProps } = this.context
			const selected = extraProps['selected']
			values['style'] = {height: `${this.state.size}px`}
			values['value'] = selected && `${selected['start_time'].format('h:mm')} - ${selected['end_time'].format('h:mma')}`
		}
		return values
	}

	renderContent() {
		const {
			children,
			accessors,
			components,
			getters,
			slotMetrics,
			localizer,
		} = this.props

		let { event, top, height, preview } = this.state
		if (!event) return children

		const events = children.props.children
		const { start, end } = event

		let label
		let format = 'eventTimeRangeFormat'

		const startsBeforeDay = slotMetrics.startsBeforeDay(start)
		const startsAfterDay = slotMetrics.startsAfterDay(end)

		if (startsBeforeDay) format = 'eventTimeRangeEndFormat'
		else if (startsAfterDay) format = 'eventTimeRangeStartFormat'

		if (startsBeforeDay && startsAfterDay) label = localizer.messages.allDay
		else label = localizer.format({ start, end }, format)

		// console.log('[EventContainerWrapper]',event)
		// Handle preview style

		return React.cloneElement(children, {
			children: (
				<React.Fragment>
					{events}
					{event && (
						<TimeGridEvent
							event={event}
							label={label}
							className="rbc-addons-dnd-drag-preview"
							style={{ top, height, width: 100 }}
							getters={getters}
							components={{ ...components, eventWrapper: NoopWrapper }}
							accessors={{ ...accessors, ...dragAccessors }}
							continuesEarlier={startsBeforeDay}
							continuesLater={startsAfterDay}
						/>
					)}
				</React.Fragment>
			),
		})
	}

	////////////////////////
	// Time Slot handlers //
	////////////////////////
	onSlotDown = (id, event) => {
		// console.log('[SLOTDOWN]')
		// Find correct date based on position
		const boundingRect = event.currentTarget.getBoundingClientRect()
		const clientOffset = {
			x: boundingRect.x,
			y: window.event.clientY 
		}
		const slot = this.getSlot(clientOffset)
		const size = slot['size']
		const top = ~~((slot['offsetY'])/size)*size
		this.setState({
			top: top,
			size: size*2,
			bottom: top + (size*2),
			date: slot['schedule']['start_date']
		})


		// Callback to Calendar.js
		// const start_date = set(slot['schedule']['start_time'], {hours: 0, minutes: 0, seconds: 0})
		const start_date = slot['schedule']['start_time'].clone().set({hour: 0, minute: 0, seccond: 0})
		const schedule = {
			start_date: start_date,
			start_time: slot['schedule']['start_time'],
			end_date: start_date,
			// end_time: add(slot['schedule']['start_time'], {hours: 1})
			end_time: slot['schedule']['start_time'].clone().add({hours: 1})
		}
		this.context.slotHandlers.onSlotDown && this.context.slotHandlers.onSlotDown(schedule, event)
	}

	onSlotHover = (id, event) => {
		// console.log('[SLOTHOVER]')
		const { draggable, slotHandlers, extraProps } = this.context
		const schedule = extraProps['selected']
		const scheduleSlot = extraProps['slot']

		// Find correct date based on position
		const boundingRect = event.currentTarget.getBoundingClientRect()
		const clientOffset = {
			x: boundingRect.x,
			y: window.event.clientY 
		}

		// Find correct schedule to update
		const slot = this.getSlot(clientOffset)
		const slotDate = slot['schedule']['end_time']
		let top = this.state.top
		let bottom = this.state.bottom
		let position = this.state.position

		if(!schedule['start_time'] || !schedule['end_time']){
			return
		}

		try {
			if(slotDate.isBefore(schedule['start_time'])){
				schedule['start_time'] = slot['schedule']['end_time']
				schedule['start_date'] = slot['schedule']['end_time'].clone().set({hour: 0, minute: 0, second: 0})
				top = slot['offsetY']
				position = 'top-start'
			} 
	
			else if (slotDate.isAfter(schedule['end_time'])){
				schedule['end_time'] = slot['schedule']['end_time']
				schedule['end_date'] = slot['schedule']['end_time'].clone().set({hour: 0, minute: 0, second: 0})
				bottom = slot['offsetY']
				position = 'bottom-start'
			} 
			else if(slotDate.isBefore(schedule['end_time'])& slotDate.isAfter(schedule['start_time'])) {
				const closest = closestTo(scheduleSlot.toDate(), [schedule['start_time'].toDate(), schedule['end_time'].toDate()])
				if(isEqual(closest, schedule['end_time'].toDate())){
					if(differenceInMinutes(slotDate.toDate(), schedule['start_time'].toDate()) >= 60){
						schedule['end_time'] = slot['schedule']['end_time']
						schedule['end_time'] = slot['schedule']['end_time'].clone().set({hour: 0, minute: 0, second: 0})
						bottom = slot['offsetY']
						position = 'bottom-start'
					}
				} 
				
				else if (isEqual(closest, schedule['start_time'].toDate())){
					if(differenceInMinutes(schedule['end_time'].toDate(), slotDate.toDate()) >= 60){
						schedule['start_time'] = slot['schedule']['end_time']
						schedule['start_date'] = slot['schedule']['end_time'].clone().set({hour: 0, minute: 0, second: 0})
						top = slot['offsetY']
						position = 'top-start'
					}
				}
			}
			
			// Final commit
			this.setState({top: top, bottom: bottom, position: position})
			slotHandlers.onSlotHover && slotHandlers.onSlotHover(schedule, event, slot['schedule']['end_time'])
		} catch {
			console.log('[EventContainerWrapper] no schedule', schedule)
		}
		
	}

	onSlotUp = (event) => {
		// console.log('[SLOTUP]')
		// Callback to Calendar.js
		const { draggable, slotHandlers, extraProps } = this.context
		slotHandlers.onSlotUp && slotHandlers.onSlotUp(this.state.position)
	}  

	render() {
		let preview = this.getPreview()
		const { draggable, slotHandlers, extraProps } = this.context
		const selected = extraProps['selected']
		const sameDate = !selected ? true : this.state.date ? isEqual(this.state.date.toDate(), selected['start_date'].toDate()) : false
		const hover = selected ? selected['start_date'] : false
		const shouldPreview = sameDate && selected
		const mouseDown = extraProps['mouseDown']
		const disable = extraProps['disable']
		// console.log(!disable)

		return (
			<Droppable id='eventWrapper' accept={['ITEM', 'SCHEDULE-ITEM']}
				styles={{position: 'absolute'}}
				previewStyle={preview['style']}
				size={preview['size']}
				value={preview['value']}
				// preview={shouldPreview ? {top: this.state.top, size: this.state.size, title: preview['value']} : {}}
				preview={shouldPreview ? {top: `${this.state.top}px`, size: `${this.state.bottom - this.state.top}px`, title: preview['value']} : {}}

				onDrop={!disable && this.onDropHandler} 
				onHover={!disable && this.onHoverHandler}

				onSlotDown={!disable && this.onSlotDown}
				onSlotOver={!disable && mouseDown && this.onSlotHover}
				onSlotUp={!disable && this.onSlotUp}
				>
					<div ref={this.ref}>{this.renderContent()}</div>
			</Droppable>
		)
	}
}

const mapStateToProps = state => {
	return {
			window: state.content.window,
			body: state.content.body,
			user: state.auth.user
	}
};

const mapDispatchToProps = dispatch => {
	return {
			
	}
};

export default connect(mapStateToProps, mapDispatchToProps)(EventContainerWrapper);
